/**
 * Resolves which backend serves an API path during the Fresh → Timescale migration.
 *
 * The backend moved every Fresh-backed route down under `/fresh/**` and left an
 * alias at the original top-level path. Those aliases are replaced by Timescale
 * implementations one endpoint at a time, so a client that keeps calling the
 * top-level path would silently switch to Timescale the moment a stub is
 * replaced — with no way to opt in, compare, or roll back.
 *
 * So the client asks for `/fresh/...` explicitly. Flipping an endpoint to
 * Timescale is then a deliberate, per-endpoint change here rather than a
 * side effect of a backend deploy.
 *
 * Call sites: `withOrigin` in apiService.ts (all axios traffic) and `downloadCsv`
 * in csvExport.ts (which builds absolute URLs and bypasses axios). Between them
 * they cover 100% of API traffic.
 */

export type ApiBackend = 'fresh' | 'timescale'

/**
 * Path prefixes whose implementation moved under `/fresh` in api-insights.
 * Anything not listed here (/app, /auth, /customers, /trackers, /shipments,
 * /tasks, /health, /assets/status-columns) never moved and is passed through.
 *
 * Order matters only for readability; matching is longest-prefix.
 */
const MOVED_PREFIXES = [
  '/insights',
  '/alerts/acknowledgement',
  '/assets',
  '/company-list',
  '/fridge-alarm-codes',
  '/fridge-types',
  '/geofences',
] as const

/**
 * Paths that sit under a moved prefix but never moved themselves.
 *
 * /assets/status-columns is the user's own column layout, stored in the Timescale profile
 * and served from one place regardless of which back end the data comes from. Without this
 * it would match the /assets prefix and be rewritten to a /fresh path that does not exist.
 */
const NEVER_MOVED = ['/assets/status-columns'] as const

/**
 * Which backend each moved prefix should be served from. Everything starts on
 * `fresh`; flip an entry to `timescale` once parity for that endpoint is green.
 * Keys may be any prefix — a more specific key wins, so you can move
 * `/insights/asset/location-history` without moving all of `/insights`.
 */
const defaultBackends: Record<string, ApiBackend> = Object.fromEntries(
  MOVED_PREFIXES.map((p) => [p, 'fresh' as ApiBackend])
)

let overrides: Record<string, ApiBackend> = {}

/** Reads per-endpoint overrides, letting QA A/B a single endpoint in a live session. */
const loadLocalOverrides = (): Record<string, ApiBackend> => {
  try {
    const raw = localStorage.getItem('API_BACKEND_OVERRIDES')
    return raw ? (JSON.parse(raw) as Record<string, ApiBackend>) : {}
  } catch {
    return {}
  }
}

/**
 * Merges overrides from the server config (fetched by ConfigProvider), so an
 * environment can be flipped without a rebuild. Local storage still wins.
 */
export const setApiBackendOverrides = (fromConfig: Record<string, ApiBackend> | undefined): void => {
  overrides = { ...fromConfig, ...loadLocalOverrides() }
}

/** Longest matching prefix, so specific keys beat general ones. */
const backendFor = (pathname: string): ApiBackend | undefined => {
  if (NEVER_MOVED.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return undefined
  const table = { ...defaultBackends, ...overrides }
  let best: { len: number; backend: ApiBackend } | undefined
  for (const [prefix, backend] of Object.entries(table)) {
    const matches = pathname === prefix || pathname.startsWith(`${prefix}/`)
    if (matches && (!best || prefix.length > best.len)) {
      best = { len: prefix.length, backend }
    }
  }
  return best?.backend
}

/**
 * The query parameter an insight uses to carry its chosen data source.
 *
 * It rides along in the query string because that is the one channel every insight
 * already has: `load` builds its own path with buildQuery(parameters), so putting the
 * choice in `parameters` gets it from the config panel to here without touching any of
 * the ~26 insight modules — and it persists with the rest of the insight config for free.
 * It is stripped below, so it never reaches the server.
 */
export const DATA_SOURCE_PARAM = 'dataSource'

/** Pulls the data source out of a query string, returning the query without it. */
const takeDataSource = (query: string): { backend?: ApiBackend; rest: string } => {
  if (!query) return { rest: '' }
  const kept: string[] = []
  let backend: ApiBackend | undefined
  for (const part of query.split('&')) {
    const [key, value] = part.split('=')
    if (key === DATA_SOURCE_PARAM) {
      backend = value === 'timescale' ? 'timescale' : 'fresh'
    } else if (part) {
      kept.push(part)
    }
  }
  return { backend, rest: kept.join('&') }
}

/**
 * Maps a client-side API path to the path the backend actually serves it on, and removes
 * the dataSource marker.
 *
 * Precedence: an explicit dataSource on the request wins, because it is the tester's
 * per-insight choice; otherwise the configured backend for that prefix applies. Absolute
 * URLs, already-prefixed /fresh paths, and routes that never moved are passed through.
 */
export const resolveApiPath = (path: string): string => {
  if (/^https?:\/\//i.test(path)) return path

  // Call sites are inconsistent about the leading slash ('/assets/state/1' vs
  // 'assets/state/1'); both reach the same route because axios concatenates.
  const leadingSlash = path.startsWith('/')
  const normalized = leadingSlash ? path : `/${path}`

  const queryAt = normalized.indexOf('?')
  const pathname = queryAt === -1 ? normalized : normalized.slice(0, queryAt)
  const { backend: requested, rest } = takeDataSource(queryAt === -1 ? '' : normalized.slice(queryAt + 1))
  const withoutMarker = rest ? `${pathname}?${rest}` : pathname

  const passthrough = leadingSlash ? withoutMarker : withoutMarker.slice(1)
  if (pathname.startsWith('/fresh/')) return passthrough

  // A route with no configured backend never moved, so there is no /fresh copy of it to
  // ask for — an insight's dataSource must not drag /customers to /fresh/customers.
  const configured = backendFor(pathname)
  if (configured === undefined) return passthrough

  // The per-request choice wins over the configured default: that is the tester picking
  // a source for one insight. Timescale is served from the top-level path.
  const backend = requested ?? configured
  if (backend !== 'fresh') return passthrough

  const rewritten = `/fresh${withoutMarker}`
  return leadingSlash ? rewritten : rewritten.slice(1)
}

// Pick up any overrides stored from a previous session.
overrides = loadLocalOverrides()
